import { defaultOrganization, Organization } from "./organization";
import { defaultUser, User } from "./user";
import { defaultLocation, Location } from "./location";
import { PostPickupForm } from "./postPickupForm";
import { PickupContact } from "./pickupContact";
import { PickupEstimatedItem } from "./pickupEstimatedItem";
import { LogisticsPartner } from "./logisticsPartner";
import { defaultPickupAgent, PickupAgent } from "./pickupAgent";
import { ActiveStorageAttachment } from "./activeStorageAttachment";
import { PickupItad } from "./pickupItad";

export type SiteInformationItemType = "info" | "success" | "negative";

export type SiteInformationItem = {
  type: SiteInformationItemType;
  text: string;
};

export type SiteInformation = {
  summary: string;
  building: SiteInformationItem[];
  dockAndTruck: SiteInformationItem[];
};

export type PickupFreight = {
  id: number;
  palletsCount?: number | string;
};

export type Pickup = {
  id: number;
  uuid: string;
  orderNumber: string;
  scheduledAt: string;
  reason: string;
  organization: Organization;
  organizationId: string;
  salesRepFullName?: string | null;
  accountRepFullName?: string | null;
  user: User;
  softthinksMode: string;
  ewasteInLbs: number | "";
  countableInventoryCount?: number;
  location: Location;
  locationId: string;
  state: string;
  appleLaptopCount?: number;
  pcLaptopCount?: number;
  pickupContacts: PickupContact[];
  pickupEstimatedItems: PickupEstimatedItem[];
  pickupImages: ActiveStorageAttachment[];
  certificateOfInsuranceFile?: ActiveStorageAttachment | null;
  walkthroughPhotos: ActiveStorageAttachment[];
  onSiteLaptopPhotos: ActiveStorageAttachment[];
  onSiteInboundObjectPhotos: ActiveStorageAttachment[];
  sealedPalletPhotos: ActiveStorageAttachment[];
  inboundPalletPhotos: ActiveStorageAttachment[];
  postPickupForms: PostPickupForm[];
  internalNotes: string;
  pickupAgent?: PickupAgent;
  agent?: LogisticsPartner;
  pickupItad?: PickupItad;
  itad?: LogisticsPartner;
  freighter?: LogisticsPartner;
  pickupFreight?: PickupFreight;
  accountRep?: User;
  lotValueInCents?: number;
  estimatedLotValueInCents?: number;
  agentFees?: number;
  customerFees?: number;
  freightFees?: number;
  buybackInCents?: number;
  requestedTime?: string;
  requestedDate?: string;
  confirmedTime?: string;
  vendorCrewConfirmedAt?: string;
  comments?: string;
  siteInformation?: SiteInformation | null;
  rescheduledFrom?: string;
  rescheduledBy?: string;
  rescheduledReason?: string;
  reschedulePendingAt?: string;
  initialReportPublishedAt?: string;
  finalReportPublishedAt?: string;
  revivnCoordinator?: User;
  logisticsClickupLink?: string;
  virtualPickup?: boolean;
  repairPickup: boolean;
  returnPickup: boolean;
  indsPartsPickup: boolean;
  rebuiltFromPartsPickup: boolean;
  internalItAssetsPickup: boolean;
};

export type NewPickup = {
  id: number;
  uuid: string;
  orderNumber: string;
  scheduledAt: string;
  reason: string;
  organization?: Organization;
  organizationId: string;
  user: User;
  softthinksMode: string;
  ewasteInLbs: number | "";
  location?: Location;
  locationId: string;
  state: string;
  pickupContacts: PickupContact[];
  contacts?: PickupContact[];
  pickupEstimatedItems: PickupEstimatedItem[];
  pickupImages: ActiveStorageAttachment[];
  walkthroughPhotos: ActiveStorageAttachment[];
  onSiteLaptopPhotos: ActiveStorageAttachment[];
  onSiteInboundObjectPhotos: ActiveStorageAttachment[];
  sealedPalletPhotos: ActiveStorageAttachment[];
  inboundPalletPhotos: ActiveStorageAttachment[];
  postPickupForms: PostPickupForm[];
  internalNotes: string;
  pickupAgent?: PickupAgent;
  pickupItad?: PickupItad;
  agent?: LogisticsPartner;
  accountRep?: User;
  lotValueInCents?: number;
  buybackInCents?: number;
  requestedTime?: string;
  confirmedTime?: string;
  comments?: string;
  initialReportPublishedAt?: string;
  finalReportPublishedAt?: string;
  revivnCoordinator?: User;
  logisticsClickupLink?: string;
};
export const defaultPickup: Pickup = {
  id: 0,
  uuid: "",
  orderNumber: "",
  scheduledAt: "",
  organization: defaultOrganization,
  organizationId: "",
  locationId: "",
  user: defaultUser,
  softthinksMode: "",
  ewasteInLbs: "",
  reason: "",
  location: defaultLocation,
  state: "",
  internalNotes: "",
  pickupContacts: [],
  pickupEstimatedItems: [],
  pickupImages: [],
  walkthroughPhotos: [],
  onSiteLaptopPhotos: [],
  onSiteInboundObjectPhotos: [],
  sealedPalletPhotos: [],
  inboundPalletPhotos: [],
  postPickupForms: [],
  pickupAgent: defaultPickupAgent,
  pickupItad: undefined,
  logisticsClickupLink: "",
  repairPickup: false,
  returnPickup: false,
  rebuiltFromPartsPickup: false,
  indsPartsPickup: false,
  internalItAssetsPickup: false,
};

export interface ElasticPickup {
  id: number;
  uuid: string;
  orderNumber: string;
  scheduledAt: string;
  organizationName: string;
  state: string;
  itadName: string;
  locationName: string;
  locationAddress: string;
  locationAddress2: string;
  locationCity: string;
  locationState: string;
  locationZipcode: string;
}

export const defaultTestPickup: NewPickup = {
  id: 1,
  uuid: "6986f",
  orderNumber: "",
  scheduledAt: "",
  organization: {
    id: "1",
    uuid: "12345uuid",
    name: "Test Org",
  },
  organizationId: "1",
  locationId: "1",
  user: defaultUser,
  softthinksMode: "",
  ewasteInLbs: "",
  reason: "",
  location: defaultLocation,
  state: "",
  internalNotes: "",
  pickupContacts: [],
  pickupEstimatedItems: [],
  pickupImages: [],
  walkthroughPhotos: [],
  onSiteLaptopPhotos: [],
  onSiteInboundObjectPhotos: [],
  sealedPalletPhotos: [],
  inboundPalletPhotos: [],
  postPickupForms: [],
  pickupAgent: defaultPickupAgent,
};
